/**
 * 이상한 방 - 봉투·봉인·카드(주인공 한 장) (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스 - 떨림 · 정지 · 찢김 · 뒤집힘 · 스탬프) · 4절 8·9번(하드 피티 · 정직한 바늘)
 *
 * `ui/envelope.ts`에서 갈라져 나왔다. 이 파일은 카드 **한 장**의 노드와 그 위에 얹는 클래스·글자만 안다 -
 * 언제 떨고 언제 멎고 언제 찢는가(시각표·타이머)는 `envelopePhases.ts`가 정하고, 여기는 "떨어라"·"멎어라"·"찢어라"를
 * 받아 그린다. 길이도 인자로 들어와 CSS 변수로 나갈 뿐이다 - 그래서 이 파일에는 `setTimeout`도 시각표의 숫자도 없다.
 *
 * **등급은 찢는 순간에 처음 앉는다.** 개봉 전 구간이 네 등급 모두 같아야 긴장이 스포일러가 되지 않기 때문이고,
 * 떨림과 새는 빛은 등급을 한 번도 보지 않는다.
 *
 * ## 산출물 문구와 등급별 흔들림도 여기 있다
 *
 * `rewardLabel`은 카드에 찍히는 이름이고 사물함의 "최근 봉투" 줄도 같은 문장을 쓴다(`locker.ts`). 카드 문구의
 * 원본이 카드 파일에 있어야 두 화면이 한 물건을 다른 이름으로 부르지 않는다. `punchStepFor`는 카드가 뒤집힐 때
 * 얼마나 흔들리는가라 카드의 것이다.
 */

import type { DrawResult } from '../game/economy/envelope'
import { gradeNameKey, gradeRank, type Grade, type Reward } from '../game/economy/envelopeTable'
import { getKit } from '../game/economy/kits'
import { getLook } from '../game/economy/looks'
import { getNickname } from '../game/economy/titles'
import { t } from '../i18n'

import { createElement } from './dom'
import { punchScale } from './juice'

import './envelopeCard.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 카드 한 장. 뽑은 결과와 그것이 계정에 남긴 것. 둘 다 밖에서 계산돼 온다. */
export type EnvelopeCardView = Readonly<{
  draw: DrawResult
  /** 이미 가진 꾸밈·별명이었다. 카드에 "이미 있어서 핀 N개로" 한 줄이 더 붙는다. */
  duplicate: boolean
  /** 중복·탄창 초과로 대신 들어온 핀. 0이면 그런 일이 없었다. */
  refundPins: number
  /** 상한에 걸려 핀이 된 탄창 수. */
  overflowMagazines: number
  /** 실제로 계정에 들어간 탄창 수. 0보다 크면 상단 탄창 칩으로 날아간다. */
  magazinesGranted: number
}>

export type EnvelopeCard = Readonly<{
  /** `.ev-envelope`. 본문에 붙이는 것은 화면의 몫이다. */
  root: HTMLElement
  /**
   * 첫 상태로. 봉투는 아래에, 카드는 앉힌 채로, **등급은 아무 데도 없다.**
   *
   * 전에는 여기서 `data-grade`를 미리 입혔다(뒷면이 뒤집히기 전에는 안 보이므로 무해해 보였다).
   * 지금은 안 한다 - 개봉 전 구간이 네 등급 모두 글자 하나까지 같아야 긴장이 스포일러가 되지 않고,
   * 그 약속을 DOM 대조 테스트가 지킨다(`envelope.test.ts`의 "개봉 전은 전 등급이 같다").
   */
  reset: () => void
  /**
   * 등급·이름·사유·"N장 중 N" 글자. 언어 전환도 이 함수를 다시 부른다.
   * **찢기 직전에 처음 불린다** - 개봉 전에 뒷면 글자를 앉히면 그것이 곧 등급 유출이다.
   *
   * `position`은 연출 순서에서 **몇 번째 장인가**(1부터)다. 전에는 이 인자가 없어 총 장 수를 두 번 넘겼고,
   * 주인공 한 장만 이 줄을 보던 시절에는 그 값이 우연히 맞았다(주인공이 언제나 마지막 장이다). 열 장을
   * 한 장씩 열게 된 뒤로는 아홉 장이 전부 "10 / 10"이라고 말했다 - 우연히 맞던 값이 드러난 자리다.
   */
  paintTexts: (main: EnvelopeCardView, position: number, total: number) => void
  /** 봉투가 아래에서 올라온다(`is-up`). */
  rise: () => void
  /**
   * 떨림이 시작된다(`is-charging`). 길이는 표에서 오고 CSS는 `--ev-charge-dur`를 읽기만 한다 -
   * 떨림의 빠르기가 구간 안에서 점점 오르는 것은 키프레임의 몫이다. **등급과 무관하다.**
   */
  charge: (durationMs: number) => void
  /** 떨림이 멎고 봉인의 빛이 밝은 채로 선다(`is-held`). 이 정지가 긴장의 핵심이다. */
  hold: () => void
  /** 봉인이 찢기고 카드가 뒤집힌다. 흔들림의 크기는 등급이, 느림은 세리머니 표가 정한다. */
  tear: (grade: Grade, durationMs: number) => void
  /** 이름·등급 스탬프. */
  stamp: (durationMs: number) => void
  /** 어느 시각에서든 곧바로 끝 상태로(탭 · 움직임 줄이기). */
  jumpToDone: (grade: Grade) => void
}>

/* --- 순수 계산 ------------------------------------------------------------------ */

/**
 * 등급 → 펀치 단계(`juice.punchScale`의 콤보 단계 눈금). N은 콤보 0과 같은 미동이고, SSR은 상한(8)이다.
 * 새 배율표를 만들지 않고 콤보의 눈금을 빌리는 이유: 같은 HUD 위에서 두 종류의 "흔들림"이 서로 다른
 * 크기로 살면 어느 쪽이 큰 사건인지 몸이 헷갈린다. 등급 넷을 0·3·6·8에 놓아 SSR이 콤보 상한과 같게 한다.
 */
const PUNCH_STEP_PER_GRADE = 3

/** 등급 → 펀치 단계. `punchScale`에 그대로 들어간다. */
export function punchStepFor(grade: Grade): number {
  return gradeRank(grade) * PUNCH_STEP_PER_GRADE
}

/** 산출물 한 줄. 이름은 데이터가 든 키로 사전에서 꺼낸다(`kits.ts`·`looks.ts`·`titles.ts`의 `nameKey`). */
export function rewardLabel(reward: Reward): string {
  switch (reward.kind) {
    case 'magazines':
      return t('envelope.reward.magazines', { count: reward.count })
    case 'pins':
      return t('envelope.reward.pins', { count: reward.amount })
    case 'kit':
      return t('envelope.reward.kit', { name: t(getKit(reward.kitId).nameKey), count: reward.count })
    case 'look':
      return t('envelope.reward.look', { name: t(getLook(reward.lookId).nameKey) })
    case 'nickname':
      return t('envelope.reward.nickname', { name: t(getNickname(reward.nicknameId).nameKey) })
  }
}

/* --- 이름 맞추기 ---------------------------------------------------------------- */

/**
 * 이름을 줄일 수 있는 단계. 앞에서부터 시도해 **열 전체가 카드 안에 들어가는** 첫 단계에서 멈춘다.
 *
 * 처음에는 "이름이 두 줄을 넘으면 줄인다"였는데 그것으로는 모자랐다 - 피티 보장 줄(`.ev-card__forced`)이
 * 함께 서면 두 줄짜리 이름도 카드를 넘친다. 넘치는지 아닌지를 결정하는 것은 이름의 줄 수가 아니라
 * **그 카드에 그때 서 있는 것 전부의 합**이라, 재는 것도 그것이어야 한다.
 *
 * 글자 수로 재지 않는 이유도 같다. 한글과 라틴 문자가 차지하는 폭이 다르고("별명, 돌아앉은 것의 천적"은
 * 22px에서 두 줄인데 "Nickname, Nemesis of Turned Things"는 세 줄이다), 카탈로그가 늘 때마다 그 경계가
 * 움직인다. 그려 보고 재면 카탈로그가 또 열 배가 되어도 이 코드는 그대로다.
 */
const NAME_STEPS: readonly string[] = ['', 'is-tight', 'is-tighter']

/**
 * 카드 뒷면의 세로 열이 안쪽 여백 안에 들어가는가.
 *
 * `justify-content: center`라 넘치면 **위아래로 함께** 삐져나간다 - 그래서 아래만 보면 절반을 놓친다.
 * 흐름에 있는 것의 첫 줄과 마지막 줄만 보면 되는 이유는 가운데가 이어져 있기 때문이고, 흐름 밖의 것
 * (회차 번호)은 애초에 이 열의 일부가 아니라 뺀다.
 *
 * **잴 수 없으면 참이다.** 레이아웃이 없는 환경(jsdom)과 화면에 없는 카드는 모든 사각형이 0이라
 * 그대로 참이 되고, 그때는 줄이지 않는다 - "모른다"를 "넘쳤다"로 읽어 글자를 줄여 두면 카드가 보일 때
 * 이유 없이 작다.
 */
function columnFits(back: HTMLElement): boolean {
  const items = [...back.children].filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement && !child.hidden && getComputedStyle(child).position !== 'absolute',
  )
  const first = items[0]
  const last = items.at(-1)
  if (first === undefined || last === undefined) return true

  const style = getComputedStyle(back)
  const box = back.getBoundingClientRect()
  const top = box.top + parseFloat(style.paddingTop)
  const bottom = box.bottom - parseFloat(style.paddingBottom)
  // 0.5px은 소수 픽셀 반올림의 여유다. 이 값으로 통과 여부가 갈리는 카드는 어차피 눈으로 같다.
  return first.getBoundingClientRect().top >= top - 0.5 && last.getBoundingClientRect().bottom <= bottom + 0.5
}

/**
 * 열이 카드 안에 앉을 때까지 이름을 한 단씩 줄인다. **줄여서 들어가는 단계가 없으면 원래 크기로 되돌린다.**
 *
 * 되돌리는 쪽이 중요하다. 가로로 눕힌 폰에서는 봉투 본문이 짧아 카드가 제 높이(`--ev-card-h`)보다
 * 눌리고, 그러면 한 줄짜리 이름("탄창 1개")조차 안 들어간다 - 거기서 마지막 단계를 그대로 두면
 * **모든 카드의 이름이 17px로 작아지면서 넘치는 것은 그대로다.** 고친 것이 아니라 두 번 나빠진 것이다.
 * 줄이는 것은 그것으로 문제가 없어질 때만 옳다.
 */
function fitName(back: HTMLElement, el: HTMLElement): void {
  for (const step of NAME_STEPS) {
    el.classList.remove('is-tight', 'is-tighter')
    if (step !== '') el.classList.add(step)
    if (columnFits(back)) return
  }
  el.classList.remove('is-tight', 'is-tighter')
}

/* --- 본체 ---------------------------------------------------------------------- */

/** 봉투 하나를 만든다. 붙이지 않는다 - 본문 안 어디에 앉는지는 화면이 정한다. */
export function createEnvelopeCard(): EnvelopeCard {
  const envelope = createElement('div', 'ev-envelope')
  const seal = createElement('div', 'ev-seal')
  seal.setAttribute('aria-hidden', 'true')
  const card = createElement('div', 'ev-card')
  const cardFront = createElement('div', 'ev-card__front')
  cardFront.setAttribute('aria-hidden', 'true')
  const cardBack = createElement('div', 'ev-card__back')
  const cardGrade = createElement('span', 'ev-card__grade', '')
  const cardForced = createElement('span', 'ev-card__forced', '')
  cardForced.hidden = true
  const cardName = createElement('strong', 'ev-card__name', '')
  const cardNote = createElement('p', 'ev-card__note', '')
  cardNote.hidden = true
  const cardIndex = createElement('span', 'ev-card__index', '')
  cardIndex.hidden = true
  cardBack.append(cardGrade, cardForced, cardName, cardNote, cardIndex)
  card.append(cardFront, cardBack)
  envelope.append(card, seal)

  /** 등급 색을 입힌다. **찢는 순간과 컷에서만** 불린다 - 개봉 전에는 등급이 DOM에 없다. */
  function wearGrade(grade: Grade): void {
    envelope.dataset.grade = grade
    card.dataset.grade = grade
  }

  return {
    root: envelope,

    reset(): void {
      envelope.className = 'ev-envelope'
      card.className = 'ev-card'
      card.style.removeProperty('--ev-punch')
      envelope.style.removeProperty('--ev-charge-dur')
      envelope.style.removeProperty('--ev-tear-dur')
      envelope.style.removeProperty('--ev-stamp-dur')
      delete envelope.dataset.grade
      delete card.dataset.grade
      // 지난 회차의 글자를 지운다. 남겨 두면 다음 봉투의 개봉 전 뒷면에 앞 회차의 등급이 적혀 있다.
      cardGrade.textContent = ''
      cardForced.textContent = ''
      cardForced.hidden = true
      cardName.textContent = ''
      cardName.classList.remove('is-tight', 'is-tighter')
      cardNote.textContent = ''
      cardNote.hidden = true
      cardIndex.textContent = ''
      cardIndex.hidden = true
    },

    paintTexts(main: EnvelopeCardView, position: number, total: number): void {
      const draw = main.draw
      cardGrade.textContent = t(gradeNameKey(draw.grade))
      cardForced.hidden = draw.forcedBy === null
      cardForced.textContent = draw.forcedBy === null ? '' : t(`envelope.forced.${draw.forcedBy}`)
      cardName.textContent = rewardLabel(draw.reward)

      // 가치 0인 봉투는 없다 - 대신 들어온 핀이 있으면 그 이유를 한 줄로 말한다.
      if (main.duplicate) cardNote.textContent = t('envelope.duplicate', { count: main.refundPins })
      else if (main.overflowMagazines > 0) cardNote.textContent = t('envelope.overflow', { count: main.refundPins })
      else cardNote.textContent = ''
      cardNote.hidden = cardNote.textContent === ''

      cardIndex.hidden = total <= 1
      cardIndex.textContent = t('envelope.cardOf', { index: position, total })

      // 맨 마지막이다 - 줄 수가 아니라 **다 놓인 뒤의 열 높이**로 재므로 모든 글자가 앉은 다음이어야 한다.
      fitName(cardBack, cardName)
    },

    rise(): void {
      envelope.classList.add('is-up')
    },

    charge(durationMs: number): void {
      envelope.style.setProperty('--ev-charge-dur', `${durationMs}ms`)
      envelope.classList.add('is-charging')
    },

    hold(): void {
      // `is-charging`을 남겨 둔 채 `is-held`를 얹는다. 떨림을 멈추는 것은 CSS의 `animation: none`이고,
      // 새어 나오던 빛은 그 자리에서 가장 밝게 굳는다 - 걷어 버리면 정지가 아니라 리셋으로 보인다.
      envelope.classList.add('is-held')
    },

    tear(grade: Grade, durationMs: number): void {
      wearGrade(grade)
      envelope.classList.remove('is-charging', 'is-held')
      envelope.style.setProperty('--ev-tear-dur', `${durationMs}ms`)
      envelope.classList.add('is-torn')
      card.style.setProperty('--ev-punch', punchScale(punchStepFor(grade)).toFixed(3))
      card.classList.add('is-flipped')
    },

    stamp(durationMs: number): void {
      envelope.style.setProperty('--ev-stamp-dur', `${durationMs}ms`)
      card.classList.add('is-stamped')
    },

    jumpToDone(grade: Grade): void {
      wearGrade(grade)
      envelope.classList.remove('is-charging', 'is-held')
      envelope.classList.add('is-up', 'is-torn')
      card.style.setProperty('--ev-punch', punchScale(punchStepFor(grade)).toFixed(3))
      card.classList.add('is-flipped', 'is-stamped')
    },
  }
}
